import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { Paged } from "src/app/common/models/paged.model";
import { AbstractService } from "src/app/common/services/abstract.service";
import { ConfigService } from "src/app/common/services/config.service";
import { DocumentsSpace, DocumentsSpaceMessage } from "src/app/modules/documents-space/documents-space.model";

const API_PATH = "/api/internal/documents-space";
@Injectable({
  providedIn: "root",
})
export class DocumentsSpaceService extends AbstractService {
  private readonly httpClient = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  async get(id: string): Promise<DocumentsSpace> {
    return await firstValueFrom(this.httpClient.get<DocumentsSpace>(`${API_PATH}/${id}`));
  }

  async edit(
    id: string,
    name: string,
    model: string,
    sharedWith: string[] = [],
    admins: string[] = [],
    filesIds: string[] = [],
  ): Promise<DocumentsSpace> {
    return await firstValueFrom(
      this.httpClient.post<DocumentsSpace>(`${API_PATH}/edit`, { id, name, model, sharedWith, admins, filesIds }),
    );
  }

  async fetchPaged(params: HttpParams): Promise<Paged<DocumentsSpace> | null> {
    const source = this.httpClient.get<Paged<DocumentsSpace>>(`${API_PATH}/paged`, { params });
    return await this.cancelPrevious("fetchPaged", source);
  }

  async create(
    name: string,
    model: string,
    sharedWith: string[] = [],
    admins: string[] = [],
    filesIds: string[] = [],
  ): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${API_PATH}/create`, { name, model, sharedWith, admins, filesIds }));
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/${id}`));
  }

  async getMessages(id: string): Promise<DocumentsSpaceMessage[]> {
    const res = await firstValueFrom(
      this.httpClient.get<{ messages: DocumentsSpaceMessage[] }>(
        `${this.configService.apiMl.httpUrl}/api/documents-space/messages`,
        { params: { documents_space_id: id } },
      ),
    );
    return res.messages;
  }

  async getOwnerMessages(id: string): Promise<DocumentsSpaceMessage[]> {
    const res = await firstValueFrom(
      this.httpClient.get<{ messages: DocumentsSpaceMessage[] }>(
        `${this.configService.apiMl.httpUrl}/api/documents-space/owner-messages`,
        { params: { documents_space_id: id } },
      ),
    );
    return res.messages;
  }

  async getFileIds(documentsSpaceId: string): Promise<string[]> {
    const res = await firstValueFrom(
      this.httpClient.get<{ file_ids: string[] }>(`${this.configService.apiMl.httpUrl}/api/documents-space/file_ids`, {
        params: { documents_space_id: documentsSpaceId },
      }),
    );
    return res.file_ids;
  }
}
